import { setErrorStatus } from "../Redux/status/action";

export async function checkAccountAvailability({ email, phoneNumber }) {
  const queryParams = [];
  if (email) {
    queryParams.push(`email=${encodeURIComponent(email)}`);
  }
  if (phoneNumber) {
    queryParams.push(`phoneNumber=${encodeURIComponent(phoneNumber)}`);
  }

  const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";

  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/profile/isExists${queryString}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        }
      }
    );

    const result = await response.json();

    if (!response.ok) {
      const errorMessage = result.error?.message || "Unknown error occurred.";
      setErrorStatus({ code: "custom", message: errorMessage });
      return null;
    }

    return result;
  } catch (error) {
    console.error("Network or server error:", error);
    throw new Error("Something went wrong");
  }
}
export async function exportFiles(
  accessToken, 
  data, 
  reportType, 
  extraParams
) {
  // "complaints" | "procedures" | "workforce" | "kpisummary" ;
  const queryParams = new URLSearchParams({ type: "excel" });
  if (data) {
    queryParams.set("filters", JSON.stringify(data));
  }
  Object.entries(extraParams || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      queryParams.set(key, value);
    }
  });
  
  const response = await fetch(
    `${process.env.REACT_APP_BASE_URL}/reports/${reportType}?${queryParams.toString()}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-auth-token": `Bearer ${accessToken}`
      }
    }
  );

  if (!response.ok) {
    const errorMessage = await response.text();
    console.error(errorMessage);
    throw new Error(`Error while fetching exporting`);
  }

  const result = await response.blob();

  if (result.error) {
    if (result.error.name === "requestError") {
      setErrorStatus({ code: "custom", message: result.error.message });
    } else {
      throw result.error;
    }
  }
  return result;
}

export async function getSummaryCount({
  accessToken,
  collection,
  subCollection,
  uniqueKey,
  query,
  type
}) {
  const response = await fetch(`${process.env.REACT_APP_BASE_URL}/summary`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-auth-token": `Bearer ${accessToken}`
    },
    body: JSON.stringify({
      ...(collection ? { collection: collection } : {}),
      ...(subCollection ? { subCollection: subCollection } : {}),
      ...(uniqueKey ? { uniqueKey: uniqueKey } : {}),
      query: query,
      type: type
    })
  });

  if (!response.ok) {
    const errorMessage = await response.text();
    console.error(errorMessage);
    throw new Error(`Error while fetching exporting`);
  }

  const result = await response.json();
  return result.data;
}

export async function submitSupportRequest({ accessToken, ...payload }) {
  const response = await fetch(`${process.env.REACT_APP_BASE_URL}/support/intake`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-auth-token": `Bearer ${accessToken}`
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const errorMessage = await response.text();
    console.error(errorMessage);
    throw new Error("Error while submitting support request");
  }

  const result = await response.json();
  return result.data;
}

export async function getSupportRequests({ accessToken }) {
  const response = await fetch(`${process.env.REACT_APP_BASE_URL}/support/requests`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-auth-token": `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const errorMessage = await response.text();
    console.error(errorMessage);
    throw new Error("Error while fetching support requests");
  }

  const result = await response.json();
  return result.data;
}

export async function getSupportRequestComments({ accessToken, workItemId }) {
  const response = await fetch(
    `${process.env.REACT_APP_BASE_URL}/support/requests/${workItemId}/comments`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-auth-token": `Bearer ${accessToken}`
      }
    }
  );

  if (!response.ok) {
    const errorMessage = await response.text();
    console.error(errorMessage);
    throw new Error("Error while fetching support request comments");
  }

  const result = await response.json();
  return result.data;
}

export async function addSupportRequestComment({ accessToken, workItemId, ...payload }) {
  const response = await fetch(
    `${process.env.REACT_APP_BASE_URL}/support/requests/${workItemId}/comments`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-auth-token": `Bearer ${accessToken}`
      },
      body: JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    const errorMessage = await response.text();
    console.error(errorMessage);
    throw new Error("Error while adding support request comment");
  }

  const result = await response.json();
  return result.data;
}

export async function closeSupportRequest({ accessToken, workItemId }) {
  const response = await fetch(
    `${process.env.REACT_APP_BASE_URL}/support/requests/${workItemId}/close`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-auth-token": `Bearer ${accessToken}`
      }
    }
  );

  if (!response.ok) {
    const errorMessage = await response.text();
    console.error(errorMessage);
    throw new Error("Error while closing support request");
  }

  const result = await response.json();
  return result.data;
}

export async function reopenSupportRequest({ accessToken, workItemId }) {
  const response = await fetch(
    `${process.env.REACT_APP_BASE_URL}/support/requests/${workItemId}/reopen`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-auth-token": `Bearer ${accessToken}`
      }
    }
  );

  if (!response.ok) {
    const errorMessage = await response.text();
    console.error(errorMessage);
    throw new Error("Error while reopening support request");
  }

  const result = await response.json();
  return result.data;
}

export async function updatePenalty(accessToken, file, reportType) {
  let data = new FormData();

  data.append("file", file);

  const response = await fetch(
    `${process.env.REACT_APP_BASE_URL}/reports/${reportType}`,
    {
      method: "POST",
      headers: {
        Accept: "*/*",
        "x-auth-token": `Bearer ${accessToken}`
      },
      body: data
    }
  );

  if (!response.ok) {
    const errorMessage = await response.text();
    console.error(errorMessage);
    throw new Error(`Error while import file`);
  }

  return await response.json();
}
